import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';

import { AppDispatch, RootState } from '../../slices';
import api from '../../helpers/api_helper';
import {
  createMarket,
  CreateMarketPayload,
} from '../../slices/market/marketSlice';

interface CreateMarketModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

interface SelectOption {
  id: number;
  name: string;
}

const initialFormData: CreateMarketPayload = {
  pos: 1,
  name: '',
  type: 0,
  current_status: 0,
  open_time: '10:00',
  close_time: '18:00',
  live_results: false,
  open_result_time: '13:00',
  close_result_time: '18:30',
  saturday_open: true,
  sunday_open: true,
  auto_results: false,
  is_active: true,
  timezone: 'Asia/Kolkata',
};

const CreateMarketModal: React.FC<CreateMarketModalProps> = ({
  isOpen,
  onClose,
  onCreated,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { loading } = useSelector((state: RootState) => state.Market);

  const [formData, setFormData] =
    useState<CreateMarketPayload>(initialFormData);

  const [marketTypes, setMarketTypes] = useState<SelectOption[]>([]);
  const [marketStatuses, setMarketStatuses] = useState<SelectOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const loadOptions = async () => {
      setOptionsLoading(true);
      setFormError('');

      try {
        const [typesResponse, statusesResponse] = await Promise.all([
          api.get('/admin/market-types/'),
          api.get('/admin/market-statuses/'),
        ]);

        const types = Array.isArray(typesResponse.data)
          ? typesResponse.data
          : [];

        const statuses = Array.isArray(statusesResponse.data)
          ? statusesResponse.data
          : [];

        setMarketTypes(types);
        setMarketStatuses(statuses);

        setFormData((previous) => ({
          ...previous,
          type: types[0]?.id ?? 0,
          current_status: statuses[0]?.id ?? 0,
        }));
      } catch (error: any) {
        setFormError(
          error.response?.data?.detail ||
            error.response?.data?.error ||
            'Unable to load market types and statuses.'
        );
      } finally {
        setOptionsLoading(false);
      }
    };

    loadOptions();
  }, [isOpen]);

  const resetAndClose = () => {
    setFormData(initialFormData);
    setFormError('');
    onClose();
  };

  const handleInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement
    >
  ) => {
    const target = event.target;
    const { name, value } = target;

    if (target instanceof HTMLInputElement && target.type === 'checkbox') {
      setFormData((previous) => ({
        ...previous,
        [name]: target.checked,
      }));
      return;
    }

    if (name === 'pos' || name === 'type' || name === 'current_status') {
      setFormData((previous) => ({
        ...previous,
        [name]: Number(value),
      }));
      return;
    }

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      return 'Market name is required.';
    }

    if (formData.pos < 1) {
      return 'Position must be at least 1.';
    }

    if (!formData.type) {
      return 'Please select a market type.';
    }

    if (!formData.current_status) {
      return 'Please select a market status.';
    }

    if (formData.open_time === formData.close_time) {
      return 'Open time and close time cannot be the same.';
    }

    if (formData.open_result_time === formData.close_result_time) {
      return 'Open result time and close result time cannot be the same.';
    }

    return '';
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const validationError = validateForm();

    if (validationError) {
      setFormError(validationError);
      return;
    }

    setFormError('');

    try {
      await dispatch(
        createMarket({
          ...formData,
          name: formData.name.trim(),
        })
      ).unwrap();

      onCreated();
      resetAndClose();
    } catch (error: any) {
      setFormError(
        typeof error === 'string'
          ? error
          : error?.detail ||
              error?.error ||
              'Unable to create the market.'
      );
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Add Market
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Create and enable a new market for this app.
            </p>
          </div>

          <button
            type="button"
            onClick={resetAndClose}
            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="max-h-[calc(90vh-85px)] overflow-y-auto p-6"
        >
          {formError && (
            <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {formError}
            </div>
          )}

          {optionsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
              <span className="ml-3 text-gray-600">
                Loading market options...
              </span>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Market Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Example: Kalyan Evening"
                    required
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Position
                  </label>
                  <input
                    type="number"
                    name="pos"
                    value={formData.pos}
                    onChange={handleInputChange}
                    min={1}
                    required
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Market Type
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    required
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  >
                    <option value={0}>Select market type</option>
                    {marketTypes.map((marketType) => (
                      <option key={marketType.id} value={marketType.id}>
                        {marketType.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Current Status
                  </label>
                  <select
                    name="current_status"
                    value={formData.current_status}
                    onChange={handleInputChange}
                    required
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  >
                    <option value={0}>Select market status</option>
                    {marketStatuses.map((marketStatus) => (
                      <option key={marketStatus.id} value={marketStatus.id}>
                        {marketStatus.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Open Time
                  </label>
                  <input
                    type="time"
                    name="open_time"
                    value={formData.open_time}
                    onChange={handleInputChange}
                    required
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Close Time
                  </label>
                  <input
                    type="time"
                    name="close_time"
                    value={formData.close_time}
                    onChange={handleInputChange}
                    required
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Open Result Time
                  </label>
                  <input
                    type="time"
                    name="open_result_time"
                    value={formData.open_result_time}
                    onChange={handleInputChange}
                    required
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Close Result Time
                  </label>
                  <input
                    type="time"
                    name="close_result_time"
                    value={formData.close_result_time}
                    onChange={handleInputChange}
                    required
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Timezone
                  </label>
                  <input
                    type="text"
                    name="timezone"
                    value={formData.timezone}
                    onChange={handleInputChange}
                    required
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 sm:grid-cols-2">
                {[
                  ['saturday_open', 'Open on Saturday'],
                  ['sunday_open', 'Open on Sunday'],
                  ['live_results', 'Live Results'],
                  ['auto_results', 'Automatic Results'],
                  ['is_active', 'Active Market'],
                ].map(([name, label]) => (
                  <label
                    key={name}
                    className="flex cursor-pointer items-center gap-3 rounded-lg bg-white px-3 py-3 shadow-sm"
                  >
                    <input
                      type="checkbox"
                      name={name}
                      checked={
                        formData[
                          name as keyof CreateMarketPayload
                        ] as boolean
                      }
                      onChange={handleInputChange}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      {label}
                    </span>
                  </label>
                ))}
              </div>

              <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-5">
                <button
                  type="button"
                  onClick={resetAndClose}
                  disabled={loading}
                  className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={loading || optionsLoading}
                  className="rounded-lg bg-blue-600 px-5 py-2.5 font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loading ? 'Creating...' : 'Create Market'}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default CreateMarketModal;